import { useCallback, useEffect, useRef, useState } from "react";

import interiorEditorApi from "../services/interiorEditorApi";
import interiorLibraryAdminApi from "../services/interiorLibraryAdminApi";

export const DEFAULT_PROJECT_PARAMETERS = {
  boardThickness: 18,
  backThickness: 8,
  defaultCabinetDepth: 560,
};

function normalizeDefinition(item) {
  return {
    id: item.id,
    code: item.code,
    name: item.name,
    nodeType: item.node_type,
    category: item.category,
    catalogItemId: item.catalog_item_id || item.catalogItemId || null,
    linkedItem: item.linked_item || item.linkedItem || null,
    linkedMounting: item.linked_mounting || item.linkedMounting || null,
    description: item.description,
    status: item.status,
    defaultParameters: item.default_parameters || {},
    geometryType: item.geometry_type || "none",
    geometryDefaults: item.geometry_defaults || {},
    frontFaceAxis: item.front_face_axis || "+z",
    anchors: item.anchors || [],
    children: item.children || [],
    profile: item.profile || null,
    sketchPlane: item.sketch_plane || "xy",
  };
}

function normalizeProject(item) {
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    status: item.status,
    scene: item.scene || {
      parameters: DEFAULT_PROJECT_PARAMETERS,
      nodes: [],
    },
  };
}

function filterInvalidNodes(tree = [], validDefinitionIds) {
  return tree
    .filter((node) => validDefinitionIds.has(String(node.definitionId)))
    .map((node) => ({
      ...node,
      children: filterInvalidNodes(node.children || [], validDefinitionIds),
    }));
}

export function collectResolvedValidationErrors(tree = []) {
  const validationErrors = [];

  function isBooleanLike(value) {
    if (value === true || value === false) return true;

    const str = String(value ?? "").trim().toLowerCase();

    return str === "true" || str === "false";
  }

  function isInvalidBooleanResultError(info) {
    const error = String(info?.error || "").toLowerCase();

    if (!error.includes("invalid result")) return false;

    return isBooleanLike(info?.raw) || isBooleanLike(info?.result);
  }

  function collectErrors(items = []) {
    items.forEach((node) => {
      Object.entries(node.parameterDebug?.trace || {}).forEach(([key, info]) => {
        if (!info?.error) return;

        // Boolean rezultati su validni za parametre kao isDoor / isDrawer.
        // Ne smeju da blokiraju save projekta.
        if (isInvalidBooleanResultError(info)) {
          return;
        }

        validationErrors.push({
          node: node.name || node.id,
          key,
          message: info.error,
          raw: info.raw,
        });
      });

      collectErrors(node.children || []);
    });
  }

  collectErrors(tree);

  return validationErrors;
}

export default function useInteriorProjectData({
  projectId,
  onLoaded,
} = {}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const onLoadedRef = useRef(onLoaded);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  const loadProject = useCallback(async () => {
    try {
      setLoading(true);

      const [projectRes, defsRes, templatesRes, categoriesRes, materialsRes] =
        await Promise.all([
          interiorEditorApi.getProject(projectId),
          interiorLibraryAdminApi.listDefinitions(),
          interiorLibraryAdminApi.listTemplates(),
          interiorLibraryAdminApi.listCategories({ scope: "template" }),
          interiorLibraryAdminApi.listMaterials(),
        ]);

      const project = normalizeProject(projectRes.item);
      const baseDefinitions = (defsRes.items || []).map(normalizeDefinition);

const definitions = await Promise.all(
  baseDefinitions.map(async (definition) => {
    try {
      const fullRes = await interiorLibraryAdminApi.getDefinition(definition.id);
      const fullDefinition = normalizeDefinition(fullRes.item);

      return {
        ...definition,
        ...fullDefinition,

        // VAŽNO: čuvamo enriched linkedItem iz getDefinition ako postoji
        linkedItem:
          fullDefinition.linkedItem ||
          definition.linkedItem ||
          null,

        linkedMounting:
          fullDefinition.linkedMounting ||
          definition.linkedMounting ||
          null,
      };
    } catch (err) {
      console.warn("load full definition failed:", {
        definitionId: definition.id,
        err,
      });

      return definition;
    }
  })
);

console.log("PROJECT FULL DEFINITIONS CHECK", {
  definitions: definitions.map((def) => ({
    id: def.id,
    name: def.name,
    nodeType: def.nodeType,
    catalogItemId: def.catalogItemId,
    linkedItemId: def.linkedItem?.id,
    linkedItemComponents: def.linkedItem?.components?.length || 0,
    linkedItemMountingItems:
      def.linkedItem?.item_mounting?.items?.length ||
      def.linkedItem?.itemMounting?.items?.length ||
      0,
    components: (def.linkedItem?.components || []).map((component) => ({
      id: component.id,
      role: component.asset_role,
      componentItemId: component.component_item?.id || component.component_item_id,
      componentItemMountingItems:
        component.component_item?.item_mounting?.items?.length ||
        component.item_mounting?.items?.length ||
        0,
      assetMountingItems:
        component.asset_mounting?.items?.length || 0,
    })),
  })),
});

      const validDefinitionIds = new Set(
        definitions.map((definition) => String(definition.id))
      );

      const nodes = filterInvalidNodes(
        Array.isArray(project.scene?.nodes) ? project.scene.nodes : [],
        validDefinitionIds
      );

      const materialList = materialsRes.items || [];

      const fullMaterials = await Promise.all(
        materialList.map(async (material) => {
          try {
            const res = await interiorLibraryAdminApi.getMaterial(material.id);
            return res.item || material;
          } catch {
            return material;
          }
        })
      );

      onLoadedRef.current?.({
        project,
        definitions,
        templates: templatesRes.items || [],
        categories: categoriesRes.items || [],
        nodes,
        projectParameters:
          project.scene?.parameters || DEFAULT_PROJECT_PARAMETERS,
        projectName: project.name || "",
        projectDescription: project.description || "",
        materials: fullMaterials,
      });
    } catch (err) {
      console.error("load project editor error:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const saveProject = useCallback(async ({
  project,
  projectName,
  projectDescription,
  projectParameters,
  room,
  nodes,
  resolvedNodes,
  documentation,
}) => {
  if (!project) {
    return { ok: false };
  }

  const validationErrors = collectResolvedValidationErrors(resolvedNodes);

  if (validationErrors.length) {
    return {
      ok: false,
      validationErrors,
    };
  }

  try {
    setSaving(true);

    await interiorEditorApi.saveProject(project.id, {
      name: projectName,
      description: projectDescription,
      status: project.status,
      scene: {
        parameters: projectParameters,
        room,
        nodes,
        documentation: documentation || null,
      },
    });

    return { ok: true };
  } catch (err) {
    console.error("save project editor error:", err);

    return {
      ok: false,
      error: err,
    };
  } finally {
    setSaving(false);
  }
}, []);

  const deleteProject = useCallback(async (project) => {
    if (!project) return { ok: false };

    try {
      await interiorEditorApi.deleteProject(project.id);
      return { ok: true };
    } catch (err) {
      console.error("delete project error:", err);
      return { ok: false, error: err };
    }
  }, []);

  return {
    loading,
    saving,
    loadProject,
    saveProject,
    deleteProject,
  };
}